/**
 * Durable, device-neutral field measurement records.
 *
 * A Room Sketch is deliberately the tablet's authoritative geometry document;
 * its sync path replaces a room's complete wall set (ADR 0022). Phone field
 * capture must therefore travel independently and be explicitly reviewed before
 * it changes a sketch. This module owns that neutral payload: stable W1…WN
 * labels, candidate/confirmed readings, and wall-hosted field features.
 *
 * The Leica adapter is intentionally absent. `manual` and `demo` prove the
 * safety and persistence contract before physical D1 evidence authorizes any
 * BLE transport (ADR 0073).
 */

const SLOT_KINDS = new Set(['perimeter_length', 'feature_dimension', 'ceiling_height', 'corner_height']);
const SLOT_STATUSES = new Set(['empty', 'candidate', 'confirmed', 'discarded', 'applied']);
const SOURCES = new Set(['manual', 'demo', 'leica_d1']);
const FEATURE_TYPES = new Set(['outlet', 'switch', 'window', 'door_opening', 'vent', 'soffit', 'other']);
const CEILING_FEATURE_TYPES = new Set(['attic_hatch', 'attic_stairs', 'ceiling_fan', 'ceiling_light', 'ceiling_sprinkler']);
const PROVENANCE = new Set(['default', 'measured', 'manual_edited']);

function idOf(makeId) {
  if (typeof makeId === 'function') return makeId();
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  throw new Error('A stable id generator is required for measurement records.');
}
function nowOf(nowIso) { return nowIso ?? new Date().toISOString(); }
function validWallLabel(value) { return /^W[1-9]\d*$/.test(String(value ?? '')); }
function clone(value) { return JSON.parse(JSON.stringify(value)); }

/** Creates the device-neutral source descriptor used by supporting capture records. */
export function createMeasurementSource(kind, device = null) {
  if (!SOURCES.has(kind)) throw new Error(`Measurement source "${kind}" is not enabled.`);
  return { kind, device: device ?? null };
}

/** Creates stable perimeter slots, independent of temporary sketch wall ids regenerated during cleanup. */
export function createWallSlots(count, { makeId, nowIso } = {}) {
  if (!Number.isInteger(count) || count < 1) throw new Error('Wall count must be a positive whole number.');
  const createdAt = nowOf(nowIso);
  return Array.from({ length: count }, (_, i) => ({
    id: idOf(makeId), wallLabel: `W${i + 1}`, kind: 'perimeter_length', status: 'empty',
    valueUnits: null, unit: 'in', source: null, capturedAt: null, candidates: [], createdAt, updatedAt: createdAt,
  }));
}

/** Creates one room ceiling target or one canonical end-corner target per wall. */
export function createHeightSlots(mode, wallCount, { makeId, nowIso } = {}) {
  if (!['flat', 'nonflat'].includes(mode)) throw new Error('Choose a flat or non-flat ceiling.');
  if (!Number.isInteger(wallCount) || wallCount < 1) throw new Error('Wall count must be a positive whole number.');
  const createdAt = nowOf(nowIso);
  const blank = (kind, label, target) => ({
    id: idOf(makeId), kind, label, target, status: 'empty', valueUnits: null, unit: 'in',
    source: null, capturedAt: null, candidates: [], createdAt, updatedAt: createdAt,
  });
  if (mode === 'flat') return [blank('ceiling_height', 'Ceiling height · flat', { scope: 'room' })];
  return Array.from({ length: wallCount }, (_, i) => blank(
    'corner_height', `Corner ${i + 1} · W${i + 1} end`, { wallLabel: `W${i + 1}`, end: 'end' },
  ));
}


/**
 * Aligns empty field targets to an accepted Shape Capture perimeter. Existing
 * readings/features are never discarded; a reduction is refused once capture
 * has begun outside the requested wall count.
 */
export function resizeWallSlots(session, count, { makeId, nowIso } = {}) {
  if (!Number.isInteger(count) || count < 1) throw new Error('Wall count must be a positive whole number.');
  const next = clone(validateSession(session));
  const at = nowOf(nowIso);
  const removed = next.slots.slice(count);
  if (removed.some((slot) => slot.status !== 'empty' && slot.status !== 'discarded')
    || next.features.some((feature) => {
      const labels = feature.hostSurface === 'ceiling' ? feature.referenceWalls : [feature.hostWallLabel];
      return labels.some((label) => Number(String(label).slice(1)) > count);
    })) {
    throw new Error('Cannot reduce wall targets after readings or features exist on those walls.');
  }
  if (next.slots.length > count) {
    next.slots = next.slots.slice(0, count);
    if (!next.slots.some((slot) => slot.id === next.armedSlotId)) next.armedSlotId = null;
  } else if (next.slots.length < count) {
    next.slots.push(...createWallSlots(count - next.slots.length, { makeId, nowIso: at }).map((slot, index) => ({
      ...slot, wallLabel: `W${next.slots.length + index + 1}`,
    })));
  }
  next.roughShape = { ...(next.roughShape ?? {}), wallCount: count };
  next.updatedAt = at;
  return next;
}

/** Creates a reviewable field session. The numbering statement travels with the session and export. */
export function createMeasurementSession({ id, roomId = null, templateId = null, slots = [], nowIso } = {}) {
  if (!id) throw new Error('Measurement session id is required.');
  const createdAt = nowOf(nowIso);
  const copiedSlots = clone(slots);
  if (new Set(copiedSlots.map((slot) => slot.id)).size !== copiedSlots.length) throw new Error('Measurement slot ids must be unique.');
  if (new Set(copiedSlots.filter((slot) => slot.kind === 'perimeter_length').map((slot) => slot.wallLabel)).size
    !== copiedSlots.filter((slot) => slot.kind === 'perimeter_length').length) throw new Error('Perimeter wall labels must be unique within a session.');
  return {
    schemaVersion: 1, id, roomId, templateId, status: 'capturing', armedSlotId: null,
    numbering: { wallOneRule: 'left_return', direction: 'clockwise' },
    slots: copiedSlots, features: [], createdAt, updatedAt: createdAt,
  };
}

/** Returns a JSON-safe copy so IndexedDB and a later sync transport share one explicit payload contract. */
export function serializeMeasurementSession(session) { return clone(validateSession(session)); }

/** Rejects malformed/future payloads rather than partially restoring a field session. */
export function deserializeMeasurementSession(payload) { return validateSession(clone(payload)); }

/** Arms exactly one target. Candidate values never choose their own destination. */
export function armMeasurementSlot(session, slotId, { nowIso } = {}) {
  const next = clone(validateSession(session));
  const slot = next.slots.find((candidate) => candidate.id === slotId);
  if (!slot) throw new Error('The selected measurement target no longer exists.');
  if (slot.status === 'applied') throw new Error('An applied measurement cannot be re-armed.');
  next.armedSlotId = slotId;
  next.updatedAt = nowOf(nowIso);
  return next;
}

/**
 * Places a candidate or confirmed source reading into the ONLY armed slot.
 * A confirmed value requires `allowOverwrite: true` before it can be replaced;
 * this is the protection against a later BLE/demo event silently changing a
 * field measurement.
 */
export function applyCandidateReading(session, { valueUnits, source, device = null, action = 'candidate', allowOverwrite = false, makeId, nowIso } = {}) {
  const next = clone(validateSession(session));
  if (!next.armedSlotId) throw new Error('Select and arm a measurement target before receiving a reading.');
  if (!Number.isFinite(valueUnits) || valueUnits <= 0) throw new Error('Measurement value must be a positive number.');
  if (!['candidate', 'accept'].includes(action)) throw new Error('Reading action must be candidate or accept.');
  const slot = next.slots.find((candidate) => candidate.id === next.armedSlotId);
  if (!slot) throw new Error('The armed measurement target no longer exists.');
  if (slot.status === 'confirmed' && !allowOverwrite) throw new Error('Confirm overwrite before replacing a confirmed measurement.');
  const at = nowOf(nowIso);
  slot.candidates = Array.isArray(slot.candidates) ? slot.candidates : [];
  if (action === 'candidate') {
    const candidate = { id: idOf(makeId), valueUnits, source: createMeasurementSource(source, device), capturedAt: at, status: 'candidate' };
    slot.candidates.push(candidate);
    slot.valueUnits = candidate.valueUnits;
    slot.source = candidate.source;
    slot.capturedAt = candidate.capturedAt;
    slot.selectedCandidateId = candidate.id;
    slot.status = 'candidate';
  } else {
    // Preserve the established direct candidate→accept API for existing callers,
    // while the phone UI normally accepts the already-selected candidate unchanged.
    if (slot.valueUnits !== valueUnits) {
      slot.valueUnits = valueUnits;
      slot.source = createMeasurementSource(source, device);
      slot.capturedAt = at;
    }
    slot.status = 'confirmed';
  }
  slot.updatedAt = at;
  next.updatedAt = at;
  return next;
}

/** Chooses one already-captured candidate without mutating the room measurement. */
export function selectCandidateReading(session, candidateId, { nowIso } = {}) {
  const next = clone(validateSession(session));
  const slot = next.slots.find((candidate) => candidate.id === next.armedSlotId);
  if (!slot || slot.status !== 'candidate') throw new Error('Arm a wall with candidate readings before choosing one.');
  const candidate = (slot.candidates ?? []).find((item) => item.id === candidateId && item.status === 'candidate');
  if (!candidate) throw new Error('The selected reading candidate no longer exists.');
  const at = nowOf(nowIso);
  slot.valueUnits = candidate.valueUnits; slot.source = candidate.source; slot.capturedAt = candidate.capturedAt;
  slot.selectedCandidateId = candidate.id; slot.updatedAt = at; next.updatedAt = at;
  return next;
}

export function discardCandidateReading(session, { candidateId = null, nowIso } = {}) {
  const next = clone(validateSession(session));
  if (!next.armedSlotId) throw new Error('Select and arm a measurement target before discarding a reading.');
  const slot = next.slots.find((candidate) => candidate.id === next.armedSlotId);
  if (!slot || slot.status !== 'candidate') throw new Error('Only a candidate reading can be discarded.');
  const at = nowOf(nowIso);
  const candidates = Array.isArray(slot.candidates) ? slot.candidates : [];
  const selected = candidates.find((item) => item.id === (candidateId ?? slot.selectedCandidateId) && item.status === 'candidate');
  if (!selected) throw new Error('The reading candidate no longer exists.');
  selected.status = 'discarded';
  const replacement = [...candidates].reverse().find((item) => item.status === 'candidate');
  if (replacement) {
    slot.valueUnits = replacement.valueUnits; slot.source = replacement.source; slot.capturedAt = replacement.capturedAt;
    slot.selectedCandidateId = replacement.id; slot.status = 'candidate';
  } else {
    slot.valueUnits = null; slot.source = null; slot.capturedAt = null; slot.selectedCandidateId = null; slot.status = 'discarded';
  }
  slot.updatedAt = at; next.updatedAt = at;
  return next;
}

/** Adds a ceiling-hosted field feature, anchored by two designer-chosen room walls. */
export function addCeilingFieldFeature(session, { id, type, referenceWalls, measurements = [], makeId, nowIso } = {}) {
  const next = clone(validateSession(session));
  if (!CEILING_FEATURE_TYPES.has(type)) throw new Error('Room obstruction type is not approved.');
  if (!Array.isArray(referenceWalls) || referenceWalls.length !== 2 || referenceWalls[0] === referenceWalls[1]
    || !referenceWalls.every((label) => next.slots.some((slot) => slot.wallLabel === label))) {
    throw new Error('A room obstruction requires two different existing W-label references.');
  }
  const at = nowOf(nowIso);
  const normalized = measurements.map((measurement) => {
    if (!measurement?.key || !Number.isFinite(measurement.valueUnits) || measurement.valueUnits < 0 || !PROVENANCE.has(measurement.provenance)) {
      throw new Error('Field feature measurements require key, non-negative value, and provenance.');
    }
    return { id: idOf(makeId), key: measurement.key, valueUnits: measurement.valueUnits, unit: 'in', provenance: measurement.provenance, status: 'confirmed' };
  });
  next.features.push({ id: id ?? idOf(makeId), type, hostSurface: 'ceiling', referenceWalls: [...referenceWalls], measurements: normalized, status: 'captured', createdAt: at, updatedAt: at });
  next.updatedAt = at;
  return next;
}

/** Adds a wall-hosted field feature with explicit default/measured/manual provenance. */
export function addFieldFeature(session, { id, type, hostWallLabel, measurements = [], makeId, nowIso } = {}) {
  const next = clone(validateSession(session));
  if (!FEATURE_TYPES.has(type)) throw new Error('Field feature type is not approved.');
  if (!next.slots.some((slot) => slot.wallLabel === hostWallLabel)) throw new Error('A field feature must be hosted on an existing W-label.');
  const at = nowOf(nowIso);
  const normalized = measurements.map((measurement) => {
    if (!measurement?.key || !Number.isFinite(measurement.valueUnits) || measurement.valueUnits < 0 || !PROVENANCE.has(measurement.provenance)) {
      throw new Error('Field feature measurements require key, non-negative value, and provenance.');
    }
    return { id: idOf(makeId), key: measurement.key, valueUnits: measurement.valueUnits, unit: 'in', provenance: measurement.provenance, status: 'confirmed' };
  });
  next.features.push({ id: id ?? idOf(makeId), type, hostWallLabel, measurements: normalized, status: 'captured', createdAt: at, updatedAt: at });
  next.updatedAt = at;
  return next;
}

function validateSession(session) {
  if (!session || session.schemaVersion !== 1 || !session.id || !Array.isArray(session.slots) || !Array.isArray(session.features)) throw new Error('Invalid measurement session payload.');
  const slotIds = new Set(); const labels = new Set();
  for (const slot of session.slots) {
    if (!slot?.id || !SLOT_KINDS.has(slot.kind) || !SLOT_STATUSES.has(slot.status) || slotIds.has(slot.id)) throw new Error('Invalid measurement slot payload.');
    if (slot.kind === 'perimeter_length') {
      if (!validWallLabel(slot.wallLabel) || labels.has(slot.wallLabel)) throw new Error('Invalid perimeter measurement slot payload.');
      labels.add(slot.wallLabel);
    } else if (slot.kind === 'ceiling_height') {
      if (slot.target?.scope !== 'room') throw new Error('Invalid ceiling measurement target.');
    } else if (slot.kind === 'corner_height') {
      if (!validWallLabel(slot.target?.wallLabel) || !['start', 'end'].includes(slot.target?.end)) throw new Error('Invalid corner measurement target.');
    }
    slotIds.add(slot.id);
  }
  if (session.armedSlotId && !slotIds.has(session.armedSlotId)) throw new Error('Armed measurement slot is invalid.');
  const featureIds = new Set();
  for (const feature of session.features) {
    if (!feature?.id || featureIds.has(feature.id) || feature.status !== 'captured' || !Array.isArray(feature.measurements)) {
      throw new Error('Invalid field feature payload.');
    }
    if (feature.hostSurface === 'ceiling') {
      if (!CEILING_FEATURE_TYPES.has(feature.type) || !Array.isArray(feature.referenceWalls)
        || feature.referenceWalls.length !== 2 || feature.referenceWalls[0] === feature.referenceWalls[1]
        || !feature.referenceWalls.every((label) => labels.has(label))) {
        throw new Error('Invalid room obstruction payload.');
      }
    } else if (!FEATURE_TYPES.has(feature.type) || !labels.has(feature.hostWallLabel)) {
      throw new Error('Invalid wall feature payload.');
    }
    for (const item of feature.measurements) {
      if (!item?.id || !item.key || !Number.isFinite(item.valueUnits) || item.valueUnits < 0 || !PROVENANCE.has(item.provenance)) {
        throw new Error('Invalid field feature measurement payload.');
      }
    }
    featureIds.add(feature.id);
  }
  return session;
}
